import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

import { API_URL } from '../utils/apiConfig';

// Single user-friendly error message - never show technical details
const FRIENDLY_ERROR_MESSAGE = 'Verification failed. Please request a new sign-in link and try again.';

/**
 * AuthVerify - Handles magic link verification at /auth/verify
 * 
 * Security: NEVER displays technical errors to users.
 * All errors show the same friendly message.
 * Technical details are logged to console only.
 */
export default function AuthVerify() {
  const [status, setStatus] = useState('loading'); // loading | error
  
  // Safely destructure auth context
  const auth = useAuth() || {};
  const { verifyMagicLink } = auth;
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        console.error('AuthVerify error: No token provided in URL');
        setStatus('error');
        return;
      }

      try {
        // Try using AuthContext's verifyMagicLink if available
        if (typeof verifyMagicLink === 'function') {
          const result = await verifyMagicLink(token);
          
          if (result && result.user) {
            const dashboardMap = {
              sender: '/app/sender',
              merchant: '/app/merchant',
              admin: '/app/admin',
            };
            const targetPath = dashboardMap[result.user.role] || '/app/sender';
            
            if (typeof navigate === 'function') {
              navigate(targetPath, { replace: true });
            } else {
              window.location.href = targetPath;
            }
            return;
          }
          
          throw new Error('Invalid response from verifyMagicLink');
        }
        
        // Fallback: call API directly
        const response = await fetch(`${API_URL}/api/auth/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        // Read body ONLY ONCE
        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error('AuthVerify error: Failed to parse response JSON', parseError);
          throw new Error('Parse error');
        }

        // Check response status AFTER parsing
        if (!response.ok) {
          console.error('AuthVerify error: API returned error', {
            status: response.status,
            detail: data?.detail || 'Unknown'
          });
          throw new Error('API error');
        }

        // Verify we got expected data
        if (!data || !data.token || !data.user) {
          console.error('AuthVerify error: Missing token or user in response');
          throw new Error('Invalid response');
        }

        // Success - store token and redirect
        try {
          localStorage.setItem('pecash_token', data.token);
        } catch (storageError) {
          console.error('AuthVerify error: Failed to store token', storageError);
        }
        
        const dashboardMap = {
          sender: '/app/sender',
          merchant: '/app/merchant',
          admin: '/app/admin',
        };
        const targetPath = dashboardMap[data.user.role] || '/app/sender';
        
        if (typeof navigate === 'function') {
          navigate(targetPath, { replace: true });
        } else {
          window.location.href = targetPath;
        }
        
      } catch (err) {
        // Log the REAL error for debugging - NEVER show to user
        console.error('AuthVerify error:', err);
        
        // Always show the same friendly error
        setStatus('error');
      }
    };

    verify();
  }, [token, verifyMagicLink, navigate]);

  // Always render visible UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
      {status === 'loading' && (
        <div className="text-center text-white" data-testid="auth-verify-loading">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Verifying your login...</h2>
          <p className="text-emerald-100 mt-2">Please wait</p>
        </div>
      )}

      {status === 'error' && (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="auth-verify-error">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
          <p className="text-slate-600 mb-6">{FRIENDLY_ERROR_MESSAGE}</p>
          <Link to="/login">
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
